import React, { useState } from "react";
import deleteIcon from "../../../../assets/images/button/deleteIcon.svg";
import deleteIconDark from "../../../../assets/images/button/deleteIconDark.svg";
import useTheme from "hooks/observer";
import { useToast } from "components/Toaster/index";
import "./spaceUsersTable.scss";
import { ExternalShare, getEmail } from "@induz/sdk";

const SpaceUsersTable = ({ searchQuery, onSearch, spaceUsers, setSpaceUsers, selectedCategory, formUserObj, onUserAdded, onUserRemoved }) => {
  const theme = useTheme();
  const showToast = useToast();
  // const [users, setUsers] = useState([
  //   { id: 1, username: "johndoe@gmail.com" },
  //   { id: 2, username: "jane.smith@gmail.com" },
  //   { id: 3, username: "mike.wilson@gmail.com" },
  //   { id: 4, username: "sarah.jones@gmail.com" },
  //   { id: 5, username: "david.brown@gmail.com" },
  //   { id: 6, username: "emma.davis@gmail.com" },
  //   { id: 7, username: "alex.taylor@gmail.com" },
  //   { id: 8, username: "lisa.anderson@gmail.com" },
  //   { id: 9, username: "tom.martin@gmail.com" },
  //   { id: 10, username: "anna.white@gmail.com"

  //    },
  //    { id: 11, username: "sarath@gmail.com"},
  //    { id: 12, username: "wisdom@gmail.com"},
  //    {id: 13, username: "honesty@gmail.com"}
  // ]);

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [emailError, setEmailError] = useState("");
  const tableContainerRef = React.useRef(null);

  // Auto-scroll to bottom when a new user is added
  React.useEffect(() => {
    if (tableContainerRef.current) {
      const container = tableContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [spaceUsers?.length]);

  const getDeleteIcon = () => {
    return theme === "dark" ? deleteIconDark : deleteIcon;
  };

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddUser = async () => {
    const trimmedEmail = newUsername.trim();
    if (!trimmedEmail) {
      return;
    }

    // Validate email format
    if (!validateEmail(trimmedEmail)) {
      showToast("Please enter a valid email address", "error");
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");

    // Check if email is the space owner (current logged-in user)
    try {
      const currentUserEmail = getEmail();
      if (currentUserEmail && currentUserEmail.toLowerCase() === trimmedEmail.toLowerCase()) {
        showToast("Cannot add space owner to the space. The space owner is already a member.", "error");
        setEmailError("Cannot add space owner to the space");
        return;
      }
    } catch (err) {
      // If getEmail fails, continue with the check (shouldn't happen in normal flow)
      console.warn("Could not get current user email:", err);
    }

    // Case-insensitive duplicate check
    const emailLower = trimmedEmail.toLowerCase();
    const userExists = spaceUsers.some(
      user => user.user_email && user.user_email.toLowerCase() === emailLower
    );

    if (userExists) {
      showToast("User already exists in this space", "error");
      setEmailError("User already exists in this space");
      return;
    }

    if (!selectedCategory) {
      showToast("Please create or select a space before adding a user.", "error");
      return;
    }

    try {
      await ExternalShare.addUserToSpace(trimmedEmail, selectedCategory);
      // Update local state immediately for better UX
      const updatedSpaceUsers = [...spaceUsers, formUserObj(trimmedEmail)];
      setSpaceUsers(updatedSpaceUsers);

      // Trigger parent component to refresh space categories from backend
      // This ensures the spaceCategories state is updated with the new user
      if (onUserAdded) {
        await onUserAdded();
      }

      setNewUsername("");
      setShowAddUserModal(false);
      showToast("User added successfully", "success");
    } catch (err) {
      console.error("Error adding user to space: ", err);
      showToast(err?.message || "Failed to add user. Please try again.", "error");
      return;
    }
  };

  const handleDeleteUser = async (userEmail) => {
    try {
      // console.log("removing user ",userEmail," from space ",selectedCategory);
      await ExternalShare.removeUserFromSpace(userEmail, selectedCategory);
      // Update local state immediately
      setSpaceUsers(prev => prev.filter(user => user.user_email !== userEmail));

      // Trigger parent component to refresh space categories from backend
      // This ensures the spaceCategories state is updated without the removed user
      if (onUserRemoved) {
        await onUserRemoved();
      }

      showToast("User removed successfully", "success");
    } catch (err) {
      console.error("Error removing user from space ", err);
      showToast(err?.message || "Failed to remove user. Please try again.", "error");
      return;
    }
  };

  if (spaceUsers == null) {
    // console.log("users is null");
  }
  // console.log("space users in space userstable ",spaceUsers);
  const filteredUsers = spaceUsers.filter(user =>
    user.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="Card SpaceUsersTable">
      <div className="CardHeader">
        <div className="HeaderTitle">
          <h3>Space Users</h3>
          {selectedCategory && (
            <span className="SpaceName">for &quot;{selectedCategory}&quot;</span>
          )}
        </div>
        <div className="Toolbar">
          <div className="Actions">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              className="SearchInput"
            />
          </div>
        </div>
        <button
          className="PrimarySm AddUserBtn"
          onClick={() => setShowAddUserModal(true)}
          disabled={!selectedCategory}
        >
          Add User
        </button>
      </div>

      <div className="CardBody">
        <div className="UsersTableContainer" ref={tableContainerRef}>
          <table className="UsersTable">
            <thead className="TableHeader">
              <tr>
                <th className="NameColumn">UserEmail</th>
                <th className="ActionColumn">Action</th>
              </tr>
            </thead>
            <tbody className="TableBody">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.user_email} className="TableRow">
                    <td className="NameColumn">
                      <span className="Username">{user.user_email}</span>
                    </td>
                    <td className="ActionColumn">
                      <div className="ActionButtons">
                        <img
                          src={getDeleteIcon()}
                          alt="Delete"
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            opacity: 0.7,
                            transition: "opacity 0.2s"
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = "1"}
                          onMouseLeave={(e) => e.target.style.opacity = "0.7"}
                          onClick={() => handleDeleteUser(user.user_email)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="TableRow">
                  <td colSpan="2" className="EmptyState">
                    {searchQuery ? "No users found matching your search" : "No users configured for this space"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="ModalOverlay">
          <div className="Modal">
            <div className="ModalHeader">
              <h3>Add New User</h3>
            </div>
            <div className="ModalBody">
              <div className="FormGroup">
                <label htmlFor="username">User Email</label>
                <input
                  id="username"
                  type="email"
                  value={newUsername}
                  placeholder="Enter user email"
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUsername(value);
                    if (emailError) setEmailError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddUser();
                    if (e.key === "Escape") {
                      setShowAddUserModal(false);
                      setNewUsername("");
                      setEmailError("");
                    }
                  }}
                />
                {emailError && (
                  <span style={{
                    color: "var(--text-error, #d32f2f)",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block"
                  }}>
                    {emailError}
                  </span>
                )}
              </div>
            </div>
            <div className="ModalFooter">
              <button
                className="SecondaryButton"
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUsername("");
                  setEmailError("");
                }}
              >
                Cancel
              </button>
              <button
                className="PrimaryButton"
                onClick={handleAddUser}
                disabled={!newUsername.trim() || !validateEmail(newUsername.trim())}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceUsersTable;
